import { useEffect, useRef, useState } from "react";
import { AsyncButton } from "./AsyncButton.js";
import { useT } from "../lib/i18n/store.js";

/**
 * "Delete account" danger zone inside the profile editor. Deletion is
 * irreversible (novels, comments, avatar, remaining credits), so it takes two
 * deliberate steps: open the confirmation, then type the account email.
 */
export function DeleteAccountSection({
  email,
  onDelete
}: {
  /** The signed-in account's email; typing it confirms the deletion. */
  email: string;
  /** Deletes the account (API) and signs out; rejects with a readable error. */
  onDelete: () => Promise<void>;
}) {
  const t = useT();
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState("");
  const [error, setError] = useState<string | null>(null);
  const matches = typed.trim().toLowerCase() === email.trim().toLowerCase();
  const sectionRef = useRef<HTMLElement>(null);

  // The profile modal is already tall; bring the opened confirmation into view.
  useEffect(() => {
    if (confirming) sectionRef.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [confirming]);

  async function handleDelete(): Promise<void> {
    setError(null);
    try {
      await onDelete();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("account.deleteFailed"));
    }
  }

  return (
    <section
      ref={sectionRef}
      className={confirming ? "danger-zone" : "danger-zone danger-zone-collapsed"}
      aria-labelledby="delete-account-heading"
    >
      <h3 id="delete-account-heading">{t("account.deleteHeading")}</h3>
      {!confirming ? (
        <button type="button" className="danger-link" onClick={() => setConfirming(true)}>
          {t("account.deleteOpen")}
        </button>
      ) : (
        <>
          <div className="danger-zone-warning">
            <p>
              <strong>{t("account.deleteIrreversible")}</strong>
            </p>
            <ul>
              <li>{t("account.deleteItemNovels")}</li>
              <li>{t("account.deleteItemComments")}</li>
              <li>{t("account.deleteItemCredits")}</li>
            </ul>
            <p>{t("account.deleteRefundNote")}</p>
          </div>
          <label className="danger-zone-confirm">
            {t("account.deleteTypeEmail", { email })}
            <input value={typed} autoComplete="off" onChange={(event) => setTyped(event.target.value)} />
          </label>
          {error && (
            <p className="invalid" role="alert">
              {error}
            </p>
          )}
          <div className="button-row">
            <AsyncButton
              type="button"
              className="danger"
              disabled={!matches}
              loadingLabel={t("account.deleting")}
              onClick={handleDelete}
            >
              {t("account.deleteConfirm")}
            </AsyncButton>
            <button type="button" className="secondary" onClick={() => setConfirming(false)}>
              {t("feedback.cancel")}
            </button>
          </div>
        </>
      )}
    </section>
  );
}
